/**
 * Function Calling Support — the schema future LLMs fill in.
 *
 * One function, `decide_creative_brief`, whose enums are read live from the
 * Creative Director's vocabulary — so adding an audience or mood upstream
 * automatically updates what the LLM is allowed to choose. Validation here
 * is deliberately forgiving: invalid or incoherent picks are DROPPED (the
 * director then chooses), never fatal, because a creative pipeline should
 * degrade to "good defaults", not crash on a hallucinated enum.
 */

import { listAudiences, listMoods, listBrandStyles, getAudience } from '../aiCreativeDirector';

export const CREATIVE_BRIEF_FUNCTION_NAME = 'decide_creative_brief';

/** OpenAI/Anthropic-style function definition, built from live vocabulary. */
export const buildCreativeBriefFunction = () => ({
  name: CREATIVE_BRIEF_FUNCTION_NAME,
  description:
    'Choose the creative brief for a flyer. Pick only values that suit the user request. ' +
    'Omit mood or brandStyle to let the Creative Director decide.',
  parameters: {
    type: 'object',
    properties: {
      audience: {
        type: 'string',
        enum: listAudiences().map((a) => a.id),
        description: 'Who the flyer is for. Required.',
      },
      mood: {
        type: 'string',
        enum: listMoods().map((m) => m.id),
        description: 'Optional emotional temperature.',
      },
      brandStyle: {
        type: 'string',
        enum: listBrandStyles().map((s) => s.id),
        description: 'Optional visual language.',
      },
      headline: {
        type: 'string',
        description: 'Optional headline copy if the user implied one.',
      },
    },
    required: ['audience'],
  },
});

/**
 * Validate + sanitize a provider's function-call arguments into a brief the
 * Creative Director accepts. Returns { brief, dropped[] } where `dropped`
 * explains every field that was discarded and why.
 */
export const parseCreativeBriefCall = (args = {}) => {
  const dropped = [];
  const audienceIds = listAudiences().map((a) => a.id);

  const audience = audienceIds.includes(args.audience) ? args.audience : null;
  if (!audience) {
    dropped.push({ field: 'audience', value: args.audience, reason: 'unknown audience — using fallback' });
  }
  const brief = { audience: audience || 'corporate' };

  if (args.mood) {
    const profile = getAudience(brief.audience);
    if (!listMoods().some((m) => m.id === args.mood)) {
      dropped.push({ field: 'mood', value: args.mood, reason: 'unknown mood' });
    } else if (!profile.moods.includes(args.mood)) {
      dropped.push({ field: 'mood', value: args.mood, reason: `incoherent with ${brief.audience} audience` });
    } else {
      brief.mood = args.mood;
    }
  }

  if (args.brandStyle) {
    const profile = getAudience(brief.audience);
    if (!listBrandStyles().some((s) => s.id === args.brandStyle)) {
      dropped.push({ field: 'brandStyle', value: args.brandStyle, reason: 'unknown brand style' });
    } else if (!profile.brandStyles.includes(args.brandStyle)) {
      dropped.push({ field: 'brandStyle', value: args.brandStyle, reason: `incoherent with ${brief.audience} audience` });
    } else {
      brief.brandStyle = args.brandStyle;
    }
  }

  if (args.headline && typeof args.headline === 'string') {
    brief.headline = args.headline.slice(0, 120);
  }

  return { brief, dropped };
};
